import { WallConfig, WallPropConfig } from "@/state/RoomConfig/WallConfig";
import { useMemo } from "react";
import { useFurnitureWithModel, useResourcesState } from "@/state/resourcesState";
import { createModelInstance } from "@/3d/modelInstance/ModelInstance";
import { FurnitureInstance } from "@/state/Furniture";
import { getBoundingRectFromMinAndSize } from "@/3d/BoundingRect";
import { Color } from "three";
import { GroupInstanceCreationOptions } from "@/3d/modelInstance/GroupInstance";
import { THEME_COLORS } from "@/utils/colors";

const MAX_SECTION_SIZE = 0.8;

export const useWindowModelInstance = (prop: WallPropConfig, wall: WallConfig) => {
    const window = useFurnitureWithModel({ name: "Окно" });
    const materials = useResourcesState((state) => state.materials);
    const isLoaded = useResourcesState((state) => state.isLoaded);

    const instance = useMemo<FurnitureInstance | undefined>(() => {
        if (!window) {
            return undefined;
        }

        const leftSectionGroup = Object.values(window.model.config.meshGroups).find(
            (group) => group.name === "LeftSection"
        );

        const rightSectionGroup = Object.values(window.model.config.meshGroups).find(
            (group) => group.name === "RightSection"
        );

        const middleSectionGroup = Object.values(window.model.config.meshGroups).find(
            (group) => group.name === "MiddleSection"
        );

        if (!leftSectionGroup || !rightSectionGroup || !middleSectionGroup) {
            return undefined;
        }

        const sectionCount = Math.ceil(prop.width / MAX_SECTION_SIZE);
        const sectionWidth = prop.width / sectionCount;

        const groupInstanceCreationOptions: GroupInstanceCreationOptions[] = [];

        for (let i = 0; i < sectionCount; i++) {
            let groupId = middleSectionGroup.id;
            if (sectionCount > 1 && i === 0) {
                groupId = rightSectionGroup.id;
            } else if (sectionCount > 2 && i === sectionCount - 1) {
                groupId = leftSectionGroup.id;
            }

            groupInstanceCreationOptions.push({
                groupId,
                sourceBoundaries: "group",
                targetBoundaries: getBoundingRectFromMinAndSize(
                    { x: 0, y: 0, z: sectionWidth * i },
                    {
                        x: window.model.raw.boundingRect.sizeX,
                        y: prop.height,
                        z: sectionWidth,
                    }
                ),
            });
        }

        const instance = createModelInstance(
            window.model.raw,
            window.model.config,
            window.model.raw.meshStats,
            materials,
            getBoundingRectFromMinAndSize(
                { x: 0, y: 0, z: 0 },
                {
                    x: window.model.raw.boundingRect.sizeX,
                    y: prop.height,
                    z: prop.width,
                }
            ),
            {
                skipUvResize: true,
                groupInstanceCreationOptions,
            }
        );

        // todo: add ability to define material slot colors to editor
        const wallColorSlot = Object.values(instance.config.materialSlots).find(
            (slot) => slot.name === "WallColor"
        );

        if (wallColorSlot) {
            instance.materialSlotMapping[wallColorSlot.id].color = wall.color;
            instance.materialSlotMapping[wallColorSlot.id].ignoreTextureColor = true;
            instance.materialSlotMapping[wallColorSlot.id].materialName = "Default";
        }

        const coloredAluminiumSlot = Object.values(instance.config.materialSlots).find(
            (slot) => slot.name === "ColoredAluminium"
        );

        if (coloredAluminiumSlot) {
            instance.materialSlotMapping[coloredAluminiumSlot.id].color = new Color(
                THEME_COLORS.primaryDark
            );
            instance.materialSlotMapping[coloredAluminiumSlot.id].materialName = "Default";
        }

        const windowGlassSlot = Object.values(instance.config.materialSlots).find(
            (slot) => slot.name === "WindowGlass"
        );

        if (windowGlassSlot) {
            instance.materialSlotMapping[windowGlassSlot.id].color = new Color(1, 1, 1);
            instance.materialSlotMapping[windowGlassSlot.id].ignoreTextureColor = true;
            instance.materialSlotMapping[windowGlassSlot.id].emissive = THEME_COLORS.generalWhite;
            instance.materialSlotMapping[windowGlassSlot.id].emissiveIntensity = 5;
            instance.materialSlotMapping[windowGlassSlot.id].materialName = "Default";
        }

        const handleSlot = Object.values(instance.config.materialSlots).find(
            (slot) => slot.name === "Handle"
        );

        if (handleSlot) {
            instance.materialSlotMapping[handleSlot.id].color = new Color(1, 1, 1);
            instance.materialSlotMapping[handleSlot.id].ignoreTextureColor = true;
            instance.materialSlotMapping[handleSlot.id].materialName = "Default";
        }

        return { id: prop.key, instance, furnitureId: window.id, type: "wallProp" };
    }, [window, materials, isLoaded, prop, wall]);

    return instance;
};
